// src/features/results/ContentSection/index.tsx

import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { Tab, TabsContainer } from "./Tabs/Tabs.styles";
import AudioAnalysisPanel from "./Panels/AudioAnalysisPanel";
import EmotionAnalysisPanel from "./Panels/EmotionAnalysisPanel";
import SeparationPanel from "./Panels/SeparationPanel";
import ConversationPanel from "./Panels/ConversationPanel/ConversationPanel"; // ✅ default import
import { designTokens } from "styles/designTokens";
import SparkleIcon from "components/SpeakerEmotionBadge/SparkleIcon";
import { ProcessResult as VUResult } from "types/output_vu";
import { ProcessResultVh as VHResult } from "types/output_vh";
import { downloadZip } from "@/apis/project/download";
import VoiceHighlightPanel from "./Panels/VoiceHighlightPanel/index";
import { sendFeedback, getFeedbackStatus } from "@/apis/project/feedback";

export type ScriptSectionTab =
  | "Voice Highlight"
  | "전체 텍스트"
  | "음원 분석"
  | "감정 분석"
  | "음원 분리"

export interface ScriptSectionProps {
  tabs: ScriptSectionTab[];
  initialTab: ScriptSectionTab;
  result?: VUResult | VHResult;
  documentId?: string;
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 32px 40px;
  background-color: ${designTokens.colors.background.primary};
  width: 100%;
  height: 100%;
  min-height: 0;
  overflow: hidden;
`;

const PanelWrapper = styled.div`
  flex: 1;
  min-height: 0;
  overflow: hidden;
  padding: 20px;
  box-sizing: border-box;
  margin: -20px; /* To compensate for the added padding and keep alignment */
`;

const ScriptSection: React.FC<ScriptSectionProps> = ({
  tabs,
  initialTab,
  result,
  documentId,
}) => {
  const [activeTab, setActiveTab] = useState<ScriptSectionTab>(initialTab);
  const [feedbackStatus, setFeedbackStatus] = useState<
    "positive" | "negative" | null
  >(null);

  useEffect(() => {
    if (documentId) {
      const fetchStatus = async () => {
        const status = await getFeedbackStatus(documentId);
        setFeedbackStatus(status);
      };
      fetchStatus();
    }
  }, [documentId]);

  // CTA나 액션 버튼 핸들러 예시
  const handleCTAClick = () => {
    // 예: 보이스 하이라이트 페이지로 이동
    window.location.href = "/upload/audion_vh";
  };

  const handleCopy = () => console.log("복사!");
  const handleShare = () => console.log("공유!");
  const handleEdit = () => console.log("수정!");
  const handleDownload = async () => {
    if (!documentId) return;
    try {
      const blob = await downloadZip(documentId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${documentId}.zip`);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to download file:", error);
      alert("파일 다운로드에 실패했습니다.");
    }
  };
  const handleLike = async () => {
    if (!documentId || feedbackStatus === "positive") return;
    try {
      await sendFeedback(documentId, "positive");
      setFeedbackStatus("positive");
    } catch (error) {
      alert("피드백 전송에 실패했습니다.");
    }
  };
  const handleDislike = async () => {
    if (!documentId || feedbackStatus === "negative") return;
    try {
      await sendFeedback(documentId, "negative");
      setFeedbackStatus("negative");
    } catch (error) {
      alert("피드백 전송에 실패했습니다.");
    }
  };

  return (
    <Wrapper>
      <TabsContainer>
        {tabs.map((tab) => (
          <Tab
            key={tab}
            $active={tab === activeTab}
            onClick={() => setActiveTab(tab)}
          >
            <SparkleIcon color={designTokens.colors.line.point} size={24} />
            <div>{tab}</div>
          </Tab>
        ))}
      </TabsContainer>

      <PanelWrapper>
        {activeTab === "Voice Highlight" && (
          <VoiceHighlightPanel
            result={result as VHResult | null}
            onDownload={handleDownload}
            onLike={handleLike}
            onDislike={handleDislike}
            feedbackStatus={feedbackStatus}
          />
        )}
        {activeTab === "전체 텍스트" && result && (
          <ConversationPanel
            result={result}
            onCopy={handleCopy}
            onShare={handleShare}
            onEdit={handleEdit}
            onDownload={handleDownload}
            onLike={handleLike}
            onDislike={handleDislike}
            feedbackStatus={feedbackStatus}
          />
        )}
        {activeTab === "음원 분석" && result && (
          <AudioAnalysisPanel
            result={result as VUResult | null}
            onCopy={() => {}}
            onShare={() => {}}
            onEdit={() => {}}
            onDownload={handleDownload}
          />
        )}
        {activeTab === "감정 분석" && result && (
          <EmotionAnalysisPanel
            result={result as VUResult | null}
            onCTAClick={handleCTAClick}
            onCopy={handleCopy}
            onShare={handleShare}
            onEdit={handleEdit}
            onDownload={handleDownload}
          />
        )}
        {activeTab === "음원 분리" && result && (
          <SeparationPanel
            result={result as VUResult | null}
            onCopy={handleCopy}
            onShare={handleShare}
            onEdit={handleEdit}
            onDownload={handleDownload}
            onLike={handleLike}
            onDislike={handleDislike}
            feedbackStatus={feedbackStatus}
          />
        )}
      </PanelWrapper>
    </Wrapper>
  );
};

export default ScriptSection;
